if (!customElements.get('all-destinations')) {
  customElements.define(
    'all-destinations',
    class extends HTMLElement {
      constructor() {
        super();
        this.groups = this.querySelectorAll('.all-destinations__group');
        this.search = this.querySelector('input[name="destinations-search"]');
        this.boundHandleGroupClick = this.handleGroupClick.bind(this);
        this.boundHandleSearchInput = this.handleSearchInput.bind(this);
        this.debouncedSearchInput = this.debounce(this.boundHandleSearchInput, 150);
        this.noSearchResults = this.querySelector('.no__search-results');
        this.boundInit = this.initialize.bind(this);
        this.scrollContainer = this.querySelector('.group__container');
        this.allLists = this.querySelectorAll('.landing__page-list');
        this.currentGroup = 'popular';
        this.renderedGroups = new Set();
      }

      connectedCallback() {
        const triggerSelector = this.dataset.lazyTrigger;
        if (triggerSelector) {
          const attachTrigger = () => {
            const triggerEl = document.querySelector(triggerSelector);
            if (triggerEl) triggerEl.addEventListener('click', this.boundInit, { once: true });
          };
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', attachTrigger, { once: true });
          } else {
            attachTrigger();
          }
          return;
        }
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', this.boundInit, { once: true });
          return;
        }
        this.initialize();
      }

      initialize() {
        this.groups.forEach((group) => {
          group.addEventListener('click', this.boundHandleGroupClick);
        });

        this.setScrollContainer();
        this.initDestinations();
        this.aggregateAllDestinations();

        const activeGroup = this.querySelector('.all-destinations__group.active');
        this.currentGroup = activeGroup?.dataset.tab || this.currentGroup;
        this.showDestinations(this.currentGroup);

        this.search?.addEventListener('input', this.debouncedSearchInput);

        const q = new URLSearchParams(window.location.search).get('q');
        if (q && this.search) {
          this.search.value = q;
          this.searchTerm = this.sanitizeSearchTerm(q);
          this.showMatchingDestinations(this.searchTerm);
        }
      }

      initDestinations() {
        try {
          const popularEl = document.querySelector('[data-dsm-popular]');
          const regionsEl = document.querySelector('[data-dsm-regions]');
          const countriesEl = document.querySelector('[data-dsm-countries]');
          if (popularEl) this.popular = JSON.parse(popularEl.textContent) || [];
          if (regionsEl) this.region = JSON.parse(regionsEl.textContent) || [];
          if (countriesEl) this.country = JSON.parse(countriesEl.textContent) || [];
        } catch (e) {
          console.warn('DL: Failed to parse pre-rendered data', e);
        }
      }

      setScrollContainer() {
        if (!this.scrollContainer) return;
        requestAnimationFrame(() => {
          this.scrollContainer.scrollLeft =
            this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth;
        });
      }

      aggregateAllDestinations() {
        const seen = new Set();
        this.all = ['popular', 'region', 'country']
          .flatMap((group) => this[group] || [])
          .filter((dest) => {
            const title = dest?.title;
            if (!title || seen.has(title)) return false;
            seen.add(title);
            return true;
          })
          .sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      }

      debounce(func, delay) {
        let timer = null;
        return (...args) => {
          if (timer) clearTimeout(timer);
          timer = setTimeout(() => func(...args), delay);
        };
      }

      handleSearchInput() {
        this.searchTerm = this.sanitizeSearchTerm(this.search?.value);

        // Filter already-rendered groups first, then switch tab.
        // This order prevents applySearchToGroup being called twice on 'all'
        // when it is first rendered by handleGroupClick on the initial search keystroke.
        this.showMatchingDestinations(this.searchTerm);

        if (this.searchTerm && this.searchTerm.length > 0) {
          this.handleGroupClick('all');
        } else if (this.currentGroup && this.searchTerm.length === 0) {
          this.handleGroupClick(this.currentGroup);
        }
      }

      sanitizeSearchTerm(searchTerm) {
        return searchTerm?.toLowerCase().replace(/[^a-z0-9\s]/g, '') || '';
      }

      // ── Matching ──────────────────────────────────────────────────────────────

      matchesDestination(dest, searchTerm) {
        const primary = (dest.search || dest.title || '').toLowerCase();
        if (this.matchesQuery(primary, searchTerm)) return true;
        if (!dest.country_list) return false;
        return dest.country_list
          .split('|')
          .some((c) => this.matchesQuery(c.toLowerCase().trim(), searchTerm));
      }

      /*
       * matchesQuery — four-tier matching (ported from destinations-search-modal.js):
       *
       * 1. Substring        — target.includes(term)
       * 2. Length guard     — no fuzzy for terms < 3 chars
       * 3. Subsequence+span — all term chars found in order within 1.5× span
       *                       catches "germny"→"germany", rejects "bali"→"balearic"
       * 4. Damerau-Levenshtein — two modes:
       *      Full word:  DL ≤ 1 (≤ 2 for terms ≥ 10 chars)
       *      Prefix:     compare term against same-length prefix of longer word;
       *                  fires when ≥ 60% of the word is typed, threshold = ceil(n/4)
       */
      matchesQuery(target, term) {
        if (!target || !term) return false;
        if (target.includes(term)) return true;
        if (term.length < 3) return false;

        let ti = 0,
          firstMatch = -1,
          lastMatch = -1;
        for (let i = 0; i < target.length && ti < term.length; i++) {
          if (target[i] === term[ti]) {
            if (firstMatch === -1) firstMatch = i;
            lastMatch = i;
            ti++;
          }
        }
        if (ti === term.length && lastMatch - firstMatch + 1 <= term.length * 1.5) return true;

        const maxDist = term.length >= 10 ? 2 : 1;
        return target.split(/[\s,()/]+/).some((word) => {
          if (word[0] !== term[0]) return false;
          if (
            Math.abs(word.length - term.length) <= maxDist &&
            this.damerauLevenshtein(term, word) <= maxDist
          )
            return true;
          if (term.length < word.length && term.length / word.length >= 0.6) {
            return (
              this.damerauLevenshtein(term, word.slice(0, term.length)) <=
              Math.ceil(term.length / 4)
            );
          }
          return false;
        });
      }

      damerauLevenshtein(a, b) {
        const m = a.length,
          n = b.length;
        const d = Array.from({ length: m + 1 }, (_, i) =>
          Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
        );
        for (let i = 1; i <= m; i++) {
          for (let j = 1; j <= n; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
              d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
            }
          }
        }
        return d[m][n];
      }

      // ── Rendering ─────────────────────────────────────────────────────────────

      normalizeUrl(url) {
        try {
          const parsed = new URL(url, window.location.origin);
          return parsed.pathname + parsed.search + parsed.hash;
        } catch {
          return url;
        }
      }

      handleize(str) {
        return (str || '')
          .toString()
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      createDestinationLink(dest) {
        const a = document.createElement('a');
        a.setAttribute('data-handle', dest.handle);
        a.href = this.normalizeUrl(dest.url);

        const li = document.createElement('li');
        li.className = 'country-destinations__item';
        li.dataset.title = this.handleize(dest.title);

        const imgWrap = document.createElement('div');
        imgWrap.className = 'list__item-img';
        const img = document.createElement('img');
        img.src = dest.icon_url || '';
        img.alt = `${dest.title} flag icon`;
        img.setAttribute('height', '150%');
        img.setAttribute('width', 'auto');
        img.className = 'list__item-flag';
        imgWrap.appendChild(img);

        const content = document.createElement('div');
        content.className = 'list__item-content';
        const titleEl = document.createElement('span');
        titleEl.className = 'list__item-title';
        titleEl.textContent = dest.title;
        content.appendChild(titleEl);

        if (dest.countries_covered > 1) {
          const price = document.createElement('div');
          price.className = 'price';
          const count = document.createElement('span');
          count.textContent = `${dest.countries_covered} Countries`;
          price.appendChild(count);
          content.appendChild(price);
        }

        li.appendChild(imgWrap);
        li.appendChild(content);
        a.appendChild(li);
        return a;
      }

      // Renders a single group's current data (filtered or full) into its list container.
      applySearchToGroup(group, searchTerm) {
        const listContainer = this.querySelector(`#${group}-list`);
        if (!listContainer) return;

        const data = this[group] || [];
        const hasSearchTerm = Boolean(searchTerm?.trim());
        const filtered = hasSearchTerm
          ? data.filter((dest) => this.matchesDestination(dest, searchTerm))
          : data;

        const fragment = document.createDocumentFragment();
        filtered.forEach((dest) => {
          if (!dest.url) return;
          fragment.appendChild(this.createDestinationLink(dest));
        });
        listContainer.replaceChildren(fragment);
      }

      showDestinations(group) {
        const listContainer = this.querySelector(`#${group}-list`);
        if (!listContainer || !this[group]) return;
        if (this.renderedGroups.has(group)) return;

        this.renderedGroups.add(group);
        this.applySearchToGroup(group, this.searchTerm);
      }

      showMatchingDestinations(searchTerm) {
        this.renderedGroups.forEach((group) => this.applySearchToGroup(group, searchTerm));
        this.updateNoSearchResultsState(Boolean(searchTerm?.trim()));
      }

      updateNoSearchResultsState(hasSearchTerm) {
        this.noSearchResults?.classList.remove('active');
        if (!hasSearchTerm) return;

        const activeList = this.querySelector('.landing__page-list.active');
        if (!activeList) return;

        if (activeList.querySelector('a')) return;

        const activeGroup = this.querySelector('.all-destinations__group.active');
        if (!activeGroup) return;

        this.noSearchResults.innerHTML = `No search results found in '${activeGroup.innerText.toLowerCase()}'. Please try searching another country or region.`;
        this.noSearchResults.classList.add('active');
        activeList.appendChild(this.noSearchResults);
      }

      handleGroupClick(event) {
        const clickedGroup = event.currentTarget || this.querySelector(`[data-tab="${event}"]`);
        if (!clickedGroup) return;

        this.showDestinations(clickedGroup.dataset.tab);

        if (event.currentTarget) {
          this.currentGroup = clickedGroup.dataset.tab;
        }

        const parentEl = this.querySelector('.group__container');
        if (clickedGroup && parentEl) {
          parentEl.scrollTo({
            left: clickedGroup.offsetLeft - 20,
            behavior: 'smooth',
          });
        }

        this.groups.forEach((group) => {
          group.classList.toggle('active', group === clickedGroup);
          if (group === clickedGroup) {
            this.handleGroupListDisplay(group);
          }
        });
      }

      handleGroupListDisplay(group) {
        const listToShow = this.querySelector(`#${group.dataset.tab}-list`);
        if (!listToShow) return;

        this.allLists?.forEach((list) => {
          list.classList.remove('active');
          if (list === listToShow) list.classList.add('active');
        });

        this.updateNoSearchResultsState(Boolean(this.searchTerm?.trim()));
      }

      disconnectedCallback() {
        document.removeEventListener('DOMContentLoaded', this.boundInit);
        this.groups.forEach((group) => {
          group.removeEventListener('click', this.boundHandleGroupClick);
        });
        this.search?.removeEventListener('input', this.debouncedSearchInput);
      }
    },
  );
}
